/**
 * PDF de Análise de Umidade (DashMedidas).
 * Layout alinhado ao padrão SGU/Relatórios — conteúdo estatístico mantido.
 */
import {
  collectUniqueProcesses,
  formatMetadataValue,
  getMeasureMetadata,
  getMeasureProcessLabel,
  metadataFieldLabel,
} from "../../../utils/measureFields";

export const generatePdfReport = async ({
  user,
  empresa,
  serialnumber,
  data,
  limits,
  stats,
  mean,
  weight,
  weightStats,
  generateStatsChart,
  generateWeightChart,
  getMeasurementDates,
  calculateStatistics,
  calculateTimeStats,
  curvaSelecionada,
  dateFormat,
  currency = "BRL",
  sacaPrice,
}) => {
  try {
    const { default: jsPDF } = await import("jspdf");

    const [statsCanvas, weightCanvas] = await Promise.all([
      generateStatsChart({
        options: {
          plugins: {
            datalabels: {
              color: "#fff",
              font: { weight: "bold", size: 10 },
              backgroundColor: (context) => context.dataset.backgroundColor,
              borderRadius: 4,
              padding: 6,
            },
            tooltip: {
              titleFont: { size: 14 },
              bodyFont: { size: 12 },
              displayColors: true,
              usePointStyle: true,
              padding: 10,
            },
          },
        },
      }),
      weight
        ? generateWeightChart({
            options: {
              plugins: {
                datalabels: {
                  color: "#fff",
                  font: { weight: "bold", size: 10 },
                  backgroundColor: (context) => context.dataset.backgroundColor,
                  borderRadius: 4,
                  padding: 6,
                },
              },
            },
          })
        : null,
    ]);

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 14;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const COLORS = {
      primary: [28, 66, 32],
      accent: [143, 173, 21],
      text: [32, 42, 36],
      muted: [95, 110, 98],
      light: [245, 248, 242],
      white: [255, 255, 255],
      line: [210, 220, 208],
      success: [143, 173, 21],
      warning: [196, 150, 30],
      danger: [180, 55, 55],
      cardBg: [236, 242, 230],
    };

    const loadImageAsDataUrl = (src) =>
      new Promise((resolve) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = image.naturalWidth || image.width;
            canvas.height = image.naturalHeight || image.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(image, 0, 0);
            resolve(canvas.toDataURL("image/png"));
          } catch {
            resolve(null);
          }
        };
        image.onerror = () => resolve(null);
        image.src = src;
      });

    const [logoWhite, logoDark] = await Promise.all([
      loadImageAsDataUrl("/images/motomco_logo_reduzida_branca.png"),
      loadImageAsDataUrl("/images/logo.png"),
    ]);

    const dateStr = new Date().toLocaleDateString("pt-BR");
    const timeStr = new Date().toLocaleTimeString("pt-BR");
    const { first, last } = getMeasurementDates(data);
    const detailedStats = calculateStatistics(data);
    const timeStats = calculateTimeStats(data);
    const equipmentLabel = data?.[0]?.nameEquipament || serialnumber || "--";
    const empresaLabel = user?.hideSensitiveInfo ? "***" : empresa || "--";
    const lastItem = data?.[data.length - 1];
    const lastProcess = getMeasureProcessLabel(lastItem);
    const processStages = collectUniqueProcesses(data);
    const lastMetadata = getMeasureMetadata(lastItem);
    const metadataLines = Object.entries(lastMetadata)
      .filter(([, value]) => formatMetadataValue(value) !== "--")
      .map(
        ([key, value]) =>
          `${metadataFieldLabel(key)}: ${formatMetadataValue(value)}`
      );

    const formatMoney = (value) => {
      try {
        return Number(value || 0).toLocaleString("pt-BR", {
          style: "currency",
          currency,
        });
      } catch {
        return `${currency === "USD" ? "US$" : "R$"} ${Number(value || 0).toFixed(2)}`;
      }
    };

    const ensureSpace = (needed) => {
      if (y + needed <= pageHeight - 18) return;
      pdf.addPage();
      drawPageHeader();
      y = 28;
    };

    const drawPageHeader = () => {
      pdf.setFillColor(...COLORS.primary);
      pdf.rect(0, 0, pageWidth, 18, "F");
      pdf.setFillColor(...COLORS.accent);
      pdf.rect(0, 18, pageWidth, 1.2, "F");
      pdf.setTextColor(...COLORS.white);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.text("Relatório de Análise de Umidade · SGU", margin, 11.5);
      if (logoWhite) {
        pdf.addImage(logoWhite, "PNG", pageWidth - margin - 38, 3.5, 36, 11);
      }
    };

    const drawFooter = () => {
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i += 1) {
        pdf.setPage(i);
        pdf.setFillColor(...COLORS.primary);
        pdf.rect(0, pageHeight - 12, pageWidth, 12, "F");
        pdf.setTextColor(...COLORS.white);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.text(
          user?.hideSensitiveInfo ? "SGU · Análise de Umidade" : empresaLabel,
          margin,
          pageHeight - 4.5
        );
        pdf.text(`Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 4.5, {
          align: "center",
        });
        pdf.text(dateStr, pageWidth - margin, pageHeight - 4.5, {
          align: "right",
        });
      }
    };

    const sectionTitle = (title) => {
      ensureSpace(16);
      pdf.setFillColor(...COLORS.light);
      pdf.roundedRect(margin, y, contentWidth, 9, 2, 2, "F");
      pdf.setFillColor(...COLORS.accent);
      pdf.roundedRect(margin, y, 2.2, 9, 1, 1, "F");
      pdf.setTextColor(...COLORS.primary);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12);
      pdf.text(title, margin + 6, y + 6);
      y += 14;
    };

    const drawKpiCard = (x, title, value, color = COLORS.primary) => {
      const width = (contentWidth - 8) / 3;
      pdf.setFillColor(...color);
      pdf.roundedRect(x, y, width, 24, 2.5, 2.5, "F");
      pdf.setTextColor(...COLORS.white);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.text(title, x + 4, y + 8);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.text(String(value), x + 4, y + 18);
    };

    const drawTable = (headers, rows, colWidths) => {
      const rowHeight = 8;
      const headerHeight = 8;

      const drawHeader = () => {
        ensureSpace(headerHeight + rowHeight);
        let x = margin;
        pdf.setFillColor(...COLORS.primary);
        pdf.roundedRect(margin, y, contentWidth, headerHeight, 1.5, 1.5, "F");
        pdf.setTextColor(...COLORS.white);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8);
        headers.forEach((header, index) => {
          pdf.text(header, x + colWidths[index] / 2, y + 5.2, {
            align: "center",
          });
          x += colWidths[index];
        });
        y += headerHeight;
      };

      drawHeader();

      rows.forEach((row, rowIndex) => {
        if (y + rowHeight > pageHeight - 18) {
          pdf.addPage();
          drawPageHeader();
          y = 28;
          drawHeader();
        }

        if (rowIndex % 2 === 0) {
          pdf.setFillColor(...COLORS.light);
          pdf.rect(margin, y, contentWidth, rowHeight, "F");
        }

        let x = margin;
        pdf.setTextColor(...COLORS.text);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        row.forEach((cell, index) => {
          const text = String(cell ?? "--");
          const maxWidth = Math.max(8, colWidths[index] - 3);
          const clipped = pdf.splitTextToSize(text, maxWidth)[0] || text;
          pdf.text(clipped, x + colWidths[index] / 2, y + 5.2, {
            align: "center",
          });
          x += colWidths[index];
        });
        y += rowHeight;
      });
    };

    const drawBulletBlock = (items) => {
      const lines = items.filter((item) => item !== undefined && item !== null);
      const height = 8 + lines.length * 6;
      ensureSpace(height + 4);
      pdf.setFillColor(...COLORS.light);
      pdf.roundedRect(margin, y, contentWidth, height, 2, 2, "F");

      let lineY = y + 6;
      lines.forEach((item) => {
        if (item === "") {
          lineY += 3;
          return;
        }

        const isBullet = String(item).startsWith("• ");
        const isHeading = String(item).endsWith(":") && !isBullet;

        if (isHeading) {
          pdf.setTextColor(...COLORS.primary);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(9);
          pdf.text(String(item), margin + 4, lineY);
        } else if (isBullet) {
          pdf.setFillColor(...COLORS.accent);
          pdf.circle(margin + 6, lineY - 1.2, 1.1, "F");
          pdf.setTextColor(...COLORS.text);
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8.5);
          pdf.text(String(item).substring(2), margin + 10, lineY);
        } else {
          pdf.setTextColor(...COLORS.text);
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8.5);
          pdf.text(String(item), margin + 4, lineY);
        }
        lineY += 6;
      });

      y += height + 6;
    };

    // ===== CAPA =====
    pdf.setFillColor(...COLORS.primary);
    pdf.rect(0, 0, pageWidth, pageHeight, "F");
    pdf.setFillColor(...COLORS.accent);
    pdf.rect(0, 0, 8, pageHeight, "F");

    if (logoWhite) {
      pdf.addImage(logoWhite, "PNG", margin + 8, 42, 78, 24);
    } else if (logoDark) {
      pdf.addImage(logoDark, "PNG", margin + 8, 42, 55, 22);
    }

    pdf.setTextColor(...COLORS.white);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(26);
    pdf.text("Relatório de", margin + 8, 95);
    pdf.text("Análise de Umidade", margin + 8, 108);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);
    pdf.setTextColor(...COLORS.accent);
    pdf.text("SGU · DashMedidas", margin + 8, 120);

    pdf.setDrawColor(...COLORS.accent);
    pdf.setLineWidth(0.8);
    pdf.line(margin + 8, 126, margin + 70, 126);

    pdf.setTextColor(220, 230, 220);
    pdf.setFontSize(10);
    [
      `Gerado em ${dateStr} às ${timeStr}`,
      `Empresa: ${empresaLabel}`,
      `Equipamento: ${equipmentLabel}`,
      `Curva: ${curvaSelecionada || "--"}`,
    ].forEach((line, index) => {
      pdf.text(line, margin + 8, 145 + index * 8);
    });

    pdf.setFillColor(...COLORS.accent);
    pdf.roundedRect(margin + 8, pageHeight - 42, 62, 10, 2, 2, "F");
    pdf.setTextColor(...COLORS.primary);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text("CONFIDENCIAL", margin + 18, pageHeight - 35.5);

    // ===== VISÃO GERAL =====
    pdf.addPage();
    drawPageHeader();
    y = 28;

    sectionTitle("1. Visão geral");
    drawBulletBlock([
      `Empresa: ${empresaLabel}`,
      `Equipamento: ${equipmentLabel}`,
      `Curva selecionada: ${curvaSelecionada || "--"}`,
      lastProcess !== "--" ? `Etapa do processo (última medida): ${lastProcess}` : null,
      processStages.length > 1
        ? `Etapas registradas no período: ${processStages.join(" · ")}`
        : null,
      ...metadataLines,
      `Período: ${
        first && last
          ? `${dateFormat(first, "UTC:dd/mm/yy' 'HH:MM")} até ${dateFormat(
              last,
              "UTC:dd/mm/yy' 'HH:MM"
            )}`
          : "--"
      }`,
      `Gerado em ${dateStr} às ${timeStr}`,
    ].filter(Boolean));

    sectionTitle("2. Distribuição de umidade");
    ensureSpace(30);
    const cardWidth = (contentWidth - 8) / 3;
    drawKpiCard(
      margin,
      "Dentro do Limite",
      `${stats.getPercentage(stats.exact)}%`,
      COLORS.success
    );
    drawKpiCard(
      margin + cardWidth + 4,
      "Abaixo do Limite",
      `${stats.getPercentage(stats.below)}%`,
      COLORS.danger
    );
    drawKpiCard(
      margin + 2 * (cardWidth + 4),
      "Acima do Limite",
      `${stats.getPercentage(stats.above)}%`,
      COLORS.warning
    );
    y += 30;

    if (statsCanvas) {
      ensureSpace(110);
      const statsWidth = 95;
      const statsHeight = 95;
      const statsX = (pageWidth - statsWidth) / 2;
      pdf.addImage(
        statsCanvas.toDataURL("image/png"),
        "PNG",
        statsX,
        y,
        statsWidth,
        statsHeight
      );
      y += statsHeight + 8;
    }

    // ===== ANÁLISE ESTATÍSTICA =====
    sectionTitle("3. Análise estatística detalhada");
    drawTable(
      ["Métrica", "Valor", "Percentual"],
      [
        [
          "Dentro do Limite",
          `${stats.exact} medidas`,
          `${stats.getPercentage(stats.exact)}%`,
        ],
        [
          "Abaixo do Limite",
          `${stats.below} medidas`,
          `${stats.getPercentage(stats.below)}%`,
        ],
        [
          "Acima do Limite",
          `${stats.above} medidas`,
          `${stats.getPercentage(stats.above)}%`,
        ],
      ],
      [70, 55, 57]
    );
    y += 8;

    drawTable(
      ["Categoria", "Média de Umidade", "Limites"],
      [
        [
          "Dentro do Limite",
          `${mean.exactMean}%`,
          `${limits.min}% a ${limits.max}%`,
        ],
        ["Abaixo do Limite", `${mean.belowMean}%`, `< ${limits.min}%`],
        ["Acima do Limite", `${mean.aboveMean}%`, `> ${limits.max}%`],
      ],
      [70, 55, 57]
    );
    y += 8;

    sectionTitle("4. Estatísticas gerais");
    drawBulletBlock([
      `Período total: ${timeStats.days} dias e ${timeStats.hours} horas`,
      `Medições por hora: ${timeStats.avgMeasuresPerHour}`,
      `Total de medições: ${data.length}`,
    ]);

    sectionTitle("5. Métricas estatísticas");
    drawBulletBlock([
      "Medidas de Tendência Central:",
      `• Média Geral: ${mean.exactMean}%`,
      `• Mediana: ${detailedStats.median}%`,
      "",
      "Medidas de Dispersão:",
      `• Desvio Padrão: ${detailedStats.stdDev}%`,
      `• Variância: ${detailedStats.variance}`,
      `• Amplitude: ${(
        Number(detailedStats.max) - Number(detailedStats.min)
      ).toFixed(1)}%`,
      "",
      "Valores Extremos:",
      `• Mínimo Registrado: ${detailedStats.min}%`,
      `• Máximo Registrado: ${detailedStats.max}%`,
    ]);

    // ===== ANÁLISE FINANCEIRA =====
    if (weight) {
      pdf.addPage();
      drawPageHeader();
      y = 28;

      sectionTitle("6. Análise financeira");

      if (weightCanvas) {
        ensureSpace(110);
        const weightWidth = 95;
        const weightHeight = 95;
        const weightX = (pageWidth - weightWidth) / 2;
        pdf.addImage(
          weightCanvas.toDataURL("image/png"),
          "PNG",
          weightX,
          y,
          weightWidth,
          weightHeight
        );
        y += weightHeight + 8;
      }

      ensureSpace(30);
      drawKpiCard(margin, "Peso Total no Período", `${weight} ton`, COLORS.primary);
      drawKpiCard(
        margin + cardWidth + 4,
        "Peso Padrão",
        `${weightStats.inRangePercent}%`,
        COLORS.success
      );
      drawKpiCard(
        margin + 2 * (cardWidth + 4),
        "Com Desconto",
        `${weightStats.outRangePercent}%`,
        COLORS.danger
      );
      y += 30;

      drawTable(
        ["Tipo", "Peso (ton)", "Percentual"],
        [
          [
            "Peso Padrão",
            weightStats.inRange.toFixed(1),
            `${weightStats.inRangePercent}%`,
          ],
          [
            "Peso Desconto",
            weightStats.outRange.toFixed(1),
            `${weightStats.outRangePercent}%`,
          ],
          ["Total", weight, "100%"],
        ],
        [70, 55, 57]
      );
      y += 8;

      sectionTitle("Resumo financeiro");
      drawTable(
        ["Parâmetro", "Valor"],
        [
          ["Moeda", currency],
          ["Preço por Saca", formatMoney(sacaPrice)],
          ["Retorno Financeiro Estimado", formatMoney(weightStats.financialReturn)],
          [
            "Redução total (Sacos 60kg)",
            `${(weightStats.perdaTotal || 0).toLocaleString("pt-BR")}`,
          ],
        ],
        [90, 92]
      );
    }

    // ===== CONCLUSÕES =====
    pdf.addPage();
    drawPageHeader();
    y = 28;

    sectionTitle(
      weight ? "7. Conclusões e ações recomendadas" : "6. Conclusões e ações recomendadas"
    );

    ensureSpace(80);
    const statusY = y + 8;
    const centerX = pageWidth / 2;
    const radius = 28;
    const exactPercent = Number(stats.getPercentage(stats.exact)) / 100;

    pdf.setDrawColor(...COLORS.line);
    pdf.setLineWidth(3);
    pdf.circle(centerX, statusY + radius, radius, "S");

    pdf.setDrawColor(...COLORS.success);
    const segments = 100;
    for (let i = 0; i < segments * exactPercent; i += 1) {
      const a1 = (i / segments) * 2 * Math.PI - Math.PI / 2;
      const a2 = ((i + 1) / segments) * 2 * Math.PI - Math.PI / 2;
      const x1 = centerX + radius * Math.cos(a1);
      const y1 = statusY + radius + radius * Math.sin(a1);
      const x2 = centerX + radius * Math.cos(a2);
      const y2 = statusY + radius + radius * Math.sin(a2);
      pdf.line(x1, y1, x2, y2);
    }

    pdf.setTextColor(...COLORS.primary);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    const percentText = `${stats.getPercentage(stats.exact)}%`;
    pdf.text(percentText, centerX, statusY + radius - 1, { align: "center" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(...COLORS.muted);
    pdf.text("Taxa de Conformidade", centerX, statusY + radius + 7, {
      align: "center",
    });

    y = statusY + 2 * radius + 16;

    drawBulletBlock([
      "Pontos Críticos:",
      `• ${stats.getPercentage(stats.above)}% das medições acima do limite superior`,
      `• ${stats.getPercentage(stats.below)}% das medições abaixo do limite inferior`,
      `• Variabilidade do processo (Desvio Padrão): ${detailedStats.stdDev}%`,
      "",
      "Recomendações Técnicas:",
      "• Efetuar o monitoramento através de notificações por e-mail",
      "• Treinar equipe operacional",
      "",
      "Próximas Ações:",
      "• Estabelecer metas de redução de variabilidade",
      "• Realizar análises comparativas periódicas",
      "• Desenvolver plano de melhoria contínua",
    ]);

    ensureSpace(28);
    pdf.setFillColor(...COLORS.primary);
    pdf.roundedRect(margin, y, contentWidth, 24, 3, 3, "F");
    pdf.setTextColor(...COLORS.white);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.text("Observações", margin + 5, y + 8);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.text(
      "Este relatório foi gerado automaticamente a partir da análise de umidade do equipamento selecionado.",
      margin + 5,
      y + 15
    );

    drawFooter();

    return { pdf, dateStr, empresa };
  } catch (error) {
    console.error("Erro ao gerar PDF:", error);
    throw error;
  }
};
